import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;

const UserList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState(''); // كلمة مرور الأدمن نفسه (تأكيد أمنيّ)
  const [showPassword, setShowPassword] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [deleteUserId, setDeleteUserId] = useState(null);
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${apiUrl}/users`);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('error_fetching_users'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => (
      String(user.username || '').toLowerCase().includes(term)
      || String(user.first_name || '').toLowerCase().includes(term)
      || String(user.last_name || '').toLowerCase().includes(term)
      || String(user.email || '').toLowerCase().includes(term)
      || String(user.phone || '').toLowerCase().includes(term)
    ));
  }, [search, users]);

  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedUsers,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredUsers, [search]);

  const handleCloseChangePassword = useCallback(() => {
    setOpenChangePassword(false);
    setSelectedUserId(null);
    setNewPassword('');
    setAdminPassword('');
    setShowPassword(false);
  }, []);

  const handleChangePassword = useCallback(async () => {
    setError('');
    setMessage('');
    if (!newPassword || newPassword.length < 6) {
      setError(t('password_min_length'));
      return;
    }
    if (!adminPassword) {
      setError(t('admin_password_required'));
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('auth_token_missing'));
        return;
      }
      await api.put(
        `${apiUrl}/changepasswordbyadmin/${selectedUserId}/password`,
        { password: newPassword, admin_password: adminPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(t('password_changed_successfully'));
      handleCloseChangePassword();
    } catch (err) {
      setError(err?.response?.data?.message || t('password_change_failed'));
    } finally {
      setSaving(false);
    }
  }, [handleCloseChangePassword, newPassword, adminPassword, selectedUserId, t]);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(false);
    setConfirmationText('');
    setDeleteUserId(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (confirmationText.toLowerCase() !== 'delete') {
      setError(t('please_type_delete_to_confirm'));
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`${apiUrl}/users/${deleteUserId}`);
      setUsers((current) => current.filter((user) => user.user_id !== deleteUserId));
      setMessage(t('user_deleted_successfully'));
      handleCloseDeleteConfirmation();
    } catch (err) {
      const code = err?.response?.data?.code;
      const byCode =
        code === 'PROTECTED_SYSTEM_ADMIN'
          ? t('cannot_delete_system_admin')
          : code === 'USER_REFERENCED'
            ? t('cannot_delete_referenced_user')
            : null;
      setError(byCode || err?.response?.data?.error || t('error_deleting_user'));
    } finally {
      setSaving(false);
    }
  }, [confirmationText, deleteUserId, handleCloseDeleteConfirmation, t]);

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={t('user_list')}
        subtitle={t('manage_users_permissions_hint')}
        count={users.length}
        countLabel={t('users')}
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/userregistration')} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('add_user')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('search_users')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('username')}</TableCell>
                <TableCell>{t('first_name')}</TableCell>
                <TableCell>{t('last_name')}</TableCell>
                <TableCell>{t('email')}</TableCell>
                <TableCell>{t('phone')}</TableCell>
                <TableCell>{t('permission_group') || 'Permission group'}</TableCell>
                <TableCell align="center" sx={{ minWidth: 190 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user, index) => (
                <TableRow key={user.user_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 140 }}>{user.username}</TableCell>
                  <TableCell>{user.first_name || '-'}</TableCell>
                  <TableCell>{user.last_name || '-'}</TableCell>
                  <TableCell sx={{ minWidth: 190 }}>{user.email || '-'}</TableCell>
                  <TableCell>{user.phone || '-'}</TableCell>
                  <TableCell>
                    {user.role_name || user.role_name_ar ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: COLORS.goldSoft,
                          color: COLORS.text,
                          fontWeight: 800,
                          fontSize: 12,
                        }}
                      >
                        {isRtl
                          ? (user.role_name_ar || user.role_name)
                          : (user.role_name || user.role_name_ar)}
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: COLORS.muted }}>
                        {t('no_group_assigned') || '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('view_details')}>
                        <IconButton onClick={() => navigate(`/user-details/${user.user_id}`)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}><VisibilityIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('edit_user')}>
                        <IconButton onClick={() => navigate(`/useredit/${user.user_id}`)} sx={smartActionButtonSx(COLORS.gold, COLORS.goldSoft)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton onClick={() => { setDeleteUserId(user.user_id); setDeleteConfirmationOpen(true); }} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}><DeleteIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('change_password')}>
                        <IconButton onClick={() => { setSelectedUserId(user.user_id); setOpenChangePassword(true); }} sx={smartActionButtonSx(COLORS.warning, COLORS.warningSoft)}><LockResetIcon /></IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredUsers.length && !loading && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('no_users_found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination count={filteredUsers.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Paper>

      <Dialog open={openChangePassword} onClose={handleCloseChangePassword} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('change_password')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('new_password')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((value) => !value)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label={t('admin_password_confirm')}
            type="password"
            fullWidth
            value={adminPassword}
            onChange={(event) => setAdminPassword(event.target.value)}
            helperText={t('admin_password_confirm_hint')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseChangePassword} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={handleChangePassword} disabled={saving} variant="contained" sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>{t('submit')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmationOpen} onClose={handleCloseDeleteConfirmation} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('confirm_deletion')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.muted, mb: 1 }}>{t('please_type_delete_to_confirm')}</Typography>
          <TextField autoFocus margin="dense" label={t('type_delete')} fullWidth value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteConfirmation} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={confirmDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;
